import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Seo, AdSenseUnit, Loading } from '../shared';
import ExploreHero from '../explore/ExploreHero';
import QuickFactsBar from '../explore/QuickFactsBar';
import { GUIDED_JOURNEY_META, TREK_REGIONS } from '../../data/journeys';
import { loadJourneysPage } from '../../utils/cmsContentLoader';
import { EXPLORE_IMAGES } from '../../data/exploreImages';
import { AD_SLOTS } from '../../utils/adsense';
import '../../styles/explore.scss';
import '../../styles/journey.scss';

const PER_PAGE = 24;

function coverageClass(coverage) {
	if (coverage >= 70) return 'good';
	if (coverage >= 45) return 'partial';
	return 'gap';
}

function JourneyCard({ journey }) {
	return (
		<Link
			to={`/journeys/${journey.slug}`}
			className={`journey-index__card journey-index__card--${coverageClass(journey.coverage)}`}
		>
			<span className="journey-index__card-badge">{journey.coverage >= 70 ? 'Deep' : 'Guided'}</span>
			{journey.region && <span className="journey-index__card-region">{journey.region}</span>}
			<h3 className="journey-index__card-title">{journey.title}</h3>
			<p className="journey-index__card-desc">{journey.subtitle || journey.lead}</p>
			<div className="journey-index__card-meta">
				<span>{journey.quickFacts?.[0]?.value || journey.duration_label || `${journey.steps?.length || 16} steps`}</span>
				<span className={`journey-index__coverage journey-index__coverage--${coverageClass(journey.coverage)}`}>
					{journey.coverage}% coverage
				</span>
			</div>
			<span className="journey-index__card-cta">Open journey →</span>
		</Link>
	);
}

function JourneyIndexPage() {
	const [journeys, setJourneys] = useState([]);
	const [meta, setMeta] = useState(null);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [query, setQuery] = useState('');
	const [debouncedQuery, setDebouncedQuery] = useState('');
	const [regionFilter, setRegionFilter] = useState('all');

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
		return () => clearTimeout(timer);
	}, [query]);

	const fetchPage = useCallback((page, append) => {
		const params = {
			page,
			per_page: PER_PAGE,
			...(regionFilter !== 'all' ? { region: regionFilter } : {}),
			...(debouncedQuery ? { search: debouncedQuery } : {}),
		};

		const setBusy = append ? setLoadingMore : setLoading;
		setBusy(true);

		return loadJourneysPage(params, { staticFallback: page === 1 })
			.then(({ journeys: rows, meta: pageMeta }) => {
				setJourneys((prev) => (append ? [...prev, ...rows] : rows));
				setMeta(pageMeta);
			})
			.finally(() => setBusy(false));
	}, [debouncedQuery, regionFilter]);

	useEffect(() => {
		fetchPage(1, false);
	}, [fetchPage]);

	const totalCount = meta?.total_count ?? 0;
	const hasMore = meta && (meta.current_page || 0) < (meta.total_pages || 0);

	if (loading && journeys.length === 0) return <Loading />;

	return (
		<div className="explore-page">
			<Seo
				title="Nepal Guided Journeys — Your Personal Travel Assistant"
				path="/journeys"
				description={`${totalCount || 'Interactive'} guided journeys for Nepal — progress tracking, preparation timeline, budget, documents wallet, and step-by-step tasks.`}
			/>
			<ExploreHero
				image={EXPLORE_IMAGES.himalaya}
				eyebrow="Guided Journey"
				title="A GPS for decisions"
				lead={`${totalCount || '—'} guided journeys across Nepal — same step-by-step UI, structured trek data. Start with a featured trek or browse below.`}
				compact
			/>
			<QuickFactsBar facts={[
				{ value: totalCount ? String(totalCount) : '—', label: 'Live journeys' },
				{ value: String(TREK_REGIONS.length), label: 'Regions' },
				{ value: '16', label: 'Guided steps' },
				{ value: GUIDED_JOURNEY_META.lastAudit, label: 'Last audit' },
			]} />

			<div className="container">
				<AdSenseUnit slot={AD_SLOTS.journey} className="adsense-unit--in-content" layout="journey-index" />
			</div>

			<div className="explore-body">
				<section className="discovery-section">
					<div className="container">
						<p className="lifecycle-intro">
							Need visa, seasons, or arrival info? See the{' '}
							<Link to={{ pathname: '/', hash: '#plan' }}>plan topics on the home page</Link>.
							{' '}<strong>Guided Journeys</strong> walk you through a specific trek step by step.
						</p>
						<div className="journey-index__filters">
							<input
								type="search"
								className="journey-index__search"
								placeholder="Search treks — e.g. Dolpo, Poon Hill, Mustang…"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								aria-label="Search journeys"
							/>
							<select
								className="journey-index__region-select"
								value={regionFilter}
								onChange={(e) => setRegionFilter(e.target.value)}
								aria-label="Filter by region"
							>
								<option value="all">All regions</option>
								{TREK_REGIONS.map((r) => (
									<option key={r} value={r}>{r}</option>
								))}
							</select>
						</div>
						{meta && (
							<p className="journey-index__results-meta">
								Showing {journeys.length} of {totalCount} journeys
								{debouncedQuery ? ` matching “${debouncedQuery}”` : ''}
								{regionFilter !== 'all' ? ` in ${regionFilter}` : ''}
							</p>
						)}
					</div>
				</section>

				<section className="discovery-section discovery-section--alt">
					<div className="container">
						{journeys.length > 0 ? (
							<div className="journey-index__grid">
								{journeys.map((j) => (
									<JourneyCard key={j.slug} journey={j} />
								))}
							</div>
						) : (
							<p>No journeys match your search. Try a different region or keyword.</p>
						)}

						{hasMore && (
							<div className="journey-index__load-more">
								<button
									type="button"
									className="btn btn-outline-primary"
									disabled={loadingMore}
									onClick={() => fetchPage((meta.current_page || 1) + 1, true)}
								>
									{loadingMore ? 'Loading…' : 'Load more journeys'}
								</button>
							</div>
						)}
					</div>
				</section>

				<section className="discovery-section discovery-section--alt">
					<div className="container">
						<div className="explore-cta-band">
							<h3>Start with the benchmark</h3>
							<p>Everest Base Camp is our deepest journey at 95% coverage — the template for all others.</p>
							<Link to="/journeys/everest-base-camp" className="explore-hero__cta explore-hero__cta--primary">
								Everest Base Camp Journey →
							</Link>
						</div>
					</div>
				</section>
			</div>
		</div>
	);
}

export default JourneyIndexPage;
